import React, { useState } from 'react'
import { Navigate, useNavigate, Link } from 'react-router-dom'
import { useAuth } from '../../context/AuthContext'
import { ApiError } from '../../lib/api'
import type { Me } from '../../lib/types'

// Fires the onboarding gate immediately at login, not just when the
// dashboard route is visited — a provider who hasn't finished onboarding
// never sees the dashboard at all, even for an instant.
function homeForRole(me: Me) {
  if (me.role === 'super_admin' || me.role === 'atuune_admin') return me.mfa_enrollment_required ? '/account' : '/admin'
  if (me.role === 'provider') return me.provider_onboarding_complete ? '/practice/provider' : '/practice/provider/onboarding'
  if (me.role === 'practice_owner' || me.role === 'practice_admin') return '/practice'
  if (me.role === 'patient') return '/patient'
  return '/'
}

const linkButtonStyle: React.CSSProperties = {
  background: 'none', border: 'none', color: 'inherit', textDecoration: 'underline', cursor: 'pointer', padding: 0, font: 'inherit',
}

export default function Login() {
  const { me, login, completeMfaLogin } = useAuth()
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  // Set once login() reports mfa_required — swaps the form below to the
  // code-entry step without touching the password step's markup at all.
  const [mfaChallengeToken, setMfaChallengeToken] = useState<string | null>(null)
  const [mfaCode, setMfaCode] = useState('')
  const [useRecoveryCode, setUseRecoveryCode] = useState(false)
  const [recoveryCode, setRecoveryCode] = useState('')

  if (me) return <Navigate to={homeForRole(me)} replace />

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      const result = await login(email, password)
      if ('mfaRequired' in result) {
        setMfaChallengeToken(result.mfaChallengeToken)
      } else {
        navigate(homeForRole(result), { replace: true })
      }
    } catch (err) {
      setError(err instanceof ApiError && err.status === 401 ? 'Invalid email or password.' : 'Sign in failed. Try again.')
    } finally {
      setBusy(false)
    }
  }

  async function onSubmitMfa(e: React.FormEvent) {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      const user = await completeMfaLogin(mfaChallengeToken!, useRecoveryCode ? { recovery_code: recoveryCode } : { code: mfaCode })
      navigate(homeForRole(user), { replace: true })
    } catch (err) {
      setError(err instanceof ApiError && err.status === 401 ? 'Incorrect code. Try again.' : 'Verification failed. Try again.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="auth-shell warm-theme">
      <div className="auth-card">
        {!mfaChallengeToken ? (
          <>
            <h1>Welcome back</h1>
            <p className="muted" style={{ fontSize: 13.5 }}>Sign in to your ATUUNE account.</p>
            <form onSubmit={onSubmit}>
              <div className="field">
                <label>Email</label>
                <input className="inp" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoFocus />
              </div>
              <div className="field">
                <label>Password</label>
                <input className="inp" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required />
              </div>
              {error && <div className="form-error" role="alert">{error}</div>}
              <button className="btn full" type="submit" disabled={busy}>{busy ? 'Signing in…' : 'Sign in'}</button>
            </form>
            <p className="muted" style={{ fontSize: 13, marginTop: 16 }}>
              <Link to="/reset-password/request">Forgot your password?</Link>
            </p>
            <p className="muted" style={{ fontSize: 13, marginTop: 4 }}>
              New practice? <Link to="/signup/practice">Sign up</Link> &middot; Looking for care? <Link to="/find-care">Start Intake</Link>
            </p>
          </>
        ) : (
          <>
            <h1>Two-factor verification</h1>
            <p className="muted" style={{ fontSize: 13.5 }}>
              {useRecoveryCode ? 'Enter one of your saved recovery codes.' : 'Enter the 6-digit code from your authenticator app.'}
            </p>
            <form onSubmit={onSubmitMfa}>
              {!useRecoveryCode ? (
                <div className="field">
                  <label>Authentication code</label>
                  <input
                    className="inp" inputMode="numeric" autoComplete="one-time-code" maxLength={6} autoFocus
                    value={mfaCode} onChange={(e) => setMfaCode(e.target.value)} required
                  />
                </div>
              ) : (
                <div className="field">
                  <label>Recovery code</label>
                  <input
                    className="inp" autoFocus placeholder="xxxx-xxxx-xxxx"
                    value={recoveryCode} onChange={(e) => setRecoveryCode(e.target.value)} required
                  />
                </div>
              )}
              {error && <div className="form-error" role="alert">{error}</div>}
              <button className="btn full" type="submit" disabled={busy}>{busy ? 'Verifying…' : 'Verify'}</button>
            </form>
            <p className="muted" style={{ fontSize: 13, marginTop: 16 }}>
              <button type="button" style={linkButtonStyle} onClick={() => { setUseRecoveryCode(!useRecoveryCode); setError(null) }}>
                {useRecoveryCode ? 'Use your authenticator app instead' : 'Lost your device? Use a recovery code'}
              </button>
            </p>
            <p className="muted" style={{ fontSize: 13, marginTop: 4 }}>
              <button type="button" style={linkButtonStyle} onClick={() => { setMfaChallengeToken(null); setMfaCode(''); setRecoveryCode(''); setError(null) }}>
                Back to sign in
              </button>
            </p>
          </>
        )}
      </div>
    </div>
  )
}
